import * as React from 'react';

import {PencilOutlineIcon} from 'shells/icon/svg/pencil-outline-icon';
import {
    getInvoiceActionMenuOptions,
    getQuoteActionMenuOptions,
} from 'shells/lead-sidebar/sales-documents/helpers';
import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';
import {BaseRow} from 'shells/row/base-row/base-row';
import {Body} from 'shells/typography/body';

import type {QuoteDisplayStatus} from 'nutshell-graphql-types';
import type {
    GetQuotes_quotes as Quote,
    GetInvoices_invoices as Invoice,
} from 'nutshell-graphql-types';

import {refetchForEvent} from '../../../refetch-helpers';
import {useCreateInvoiceFromQuote} from '../../../sales-documents/quotes/graphql/hooks';
import {downloadQuote} from '../../../sales-documents/shared';
import {
    useUpdateSalesDocumentStatus,
    useDuplicateSalesDocument,
} from '../../../sales-documents/shared/graphql/hooks';

import {useGetInstanceInfo} from '../../../../core/use-get-instance-info';
import {EmailModalWrapper} from '../../../../core-entity/components/email-modal-wrapper';

import {
    getSalesDocumentStatusBadgeProps,
    handleOpenSalesDocument,
    handleSalesDocumentChange,
} from './helpers';
import type {RevenueTab} from './revenue-widget';

const NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID = 'nutshell-quotes-container';

type Props = {
    tab: RevenueTab;
    setTab: (tab: RevenueTab) => void;
    leadId: string;
    quotes: Quote[];
    invoices: Invoice[];
    refetchQuotes: () => Promise<any>;
    refetchInvoices: () => Promise<any>;
};

export function SalesDocumentsTab(props: Props) {
    const [emailModalType, setEmailModalType] = React.useState<SalesDocumentType | null>(null);

    const scriptRef = React.useRef<HTMLElement | null | undefined>(null);

    const {sessionInstance} = useGetInstanceInfo();
    const {updateSalesDocumentStatus} = useUpdateSalesDocumentStatus();
    const {duplicateSalesDocument} = useDuplicateSalesDocument();
    const {createInvoiceFromQuote} = useCreateInvoiceFromQuote();

    const instanceId = sessionInstance && sessionInstance.instanceId;

    const handleSendQuoteViaEmail = React.useCallback(() => {
        setEmailModalType('Quote');
    }, []);

    const handleSendInvoiceViaEmail = React.useCallback(() => {
        setEmailModalType('Invoice');
    }, []);

    const handlePostEmailModalOpen = React.useCallback(() => {
        setEmailModalType(null);
    }, []);

    const handleDownload = (slug: string) => {
        if (!instanceId || !scriptRef.current) {
            return;
        }

        downloadQuote(instanceId, slug, scriptRef.current, NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID);
    };

    const handleUpdateStatus = (id: string, status: QuoteDisplayStatus) => {
        updateSalesDocumentStatus({
            id: id,
            status,
        }).then((response) => {
            if (NutClientConfig.hasLeadPageRedesignM3FF) {
                refetchForEvent('salesDocumentStatusChange');
            } else {
                Timeline.refreshTimeline();
            }

            const salesDocumentRes =
                response && response.data && response.data.salesDocumentUpdateStatus;

            if (
                salesDocumentRes &&
                salesDocumentRes.salesDocument &&
                props.refetchInvoices &&
                props.refetchQuotes
            ) {
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                salesDocumentRes.salesDocument.__typename === 'Quote'
                    ? props.refetchQuotes()
                    : props.refetchInvoices();
            }
        });
    };

    const handleDuplicateSalesDocument = (id: string) => {
        duplicateSalesDocument({
            salesDocument: id,
        }).then((response) => {
            const salesDocumentRes =
                response && response.data && response.data.salesDocumentDuplicate;
            if (
                salesDocumentRes &&
                salesDocumentRes.salesDocument &&
                props.refetchInvoices &&
                props.refetchQuotes
            ) {
                handleSalesDocumentChange(
                    salesDocumentRes.salesDocument.modalUrl,
                    salesDocumentRes.salesDocument.__typename === 'Quote'
                        ? props.refetchQuotes
                        : props.refetchInvoices
                );
            }
        });
    };

    const handleCreateInvoiceFromQuote = (id: string) => {
        props.setTab('invoices');

        createInvoiceFromQuote({
            quote: id,
        }).then((response) => {
            const invoiceRes = response && response.data && response.data.invoiceCreateFromQuote;
            if (invoiceRes && invoiceRes.invoice && props.refetchInvoices) {
                handleSalesDocumentChange(invoiceRes.invoice.modalUrl, props.refetchInvoices);
            }
        });
    };

    return (
        <>
            {props.tab === 'quotes'
                ? props.quotes.map((quote) => (
                      <BaseRow
                          key={quote.id}
                          type='quotes'
                          title={`Quote ${quote.displayNumber}`}
                          statusBadges={[
                              getSalesDocumentStatusBadgeProps(quote, 'Quote', quote.displayStatus),
                          ]}
                          onClick={() => handleOpenSalesDocument('Quote', quote.id)}
                          toolbarProps={{
                              options: [
                                  {
                                      id: 'edit',
                                      icon: PencilOutlineIcon,
                                      tooltipText: 'Edit quote',
                                      onClick: () => handleOpenSalesDocument('Quote', quote.id),
                                  },
                              ],
                              actionMenuOptions: {
                                  options: getQuoteActionMenuOptions(
                                      quote.id,
                                      quote.shareableViewInBrowserLink,
                                      quote.slug,
                                      quote.displayStatus,
                                      handleDownload,
                                      handleDuplicateSalesDocument,
                                      handleUpdateStatus,
                                      handleSendQuoteViaEmail,
                                      handleCreateInvoiceFromQuote
                                  ),
                              },
                          }}
                      >
                          <Body size='small'>{quote.total.formattedWithCents}</Body>
                      </BaseRow>
                  ))
                : props.invoices.map((invoice) => (
                      <BaseRow
                          key={invoice.id}
                          type='invoices'
                          title={`Invoice ${invoice.displayNumber}`}
                          statusBadges={[
                              getSalesDocumentStatusBadgeProps(
                                  invoice,
                                  'Invoice',
                                  invoice.displayStatus,
                                  invoice.overdueDuration
                              ),
                          ]}
                          onClick={() => handleOpenSalesDocument('Invoice', invoice.id)}
                          toolbarProps={{
                              options: [
                                  {
                                      id: 'edit',
                                      icon: PencilOutlineIcon,
                                      tooltipText: 'Edit invoice',
                                      onClick: () => handleOpenSalesDocument('Invoice', invoice.id),
                                  },
                              ],
                              actionMenuOptions: {
                                  options: getInvoiceActionMenuOptions(
                                      invoice.id,
                                      invoice.shareableViewInBrowserLink,
                                      invoice.slug,
                                      invoice.displayStatus,
                                      handleDownload,
                                      handleDuplicateSalesDocument,
                                      handleUpdateStatus,
                                      handleSendInvoiceViaEmail
                                  ),
                              },
                          }}
                      >
                          <Body size='small'>{invoice.total.formattedWithCents}</Body>
                      </BaseRow>
                  ))}
            {emailModalType && props.leadId && (
                <EmailModalWrapper
                    entityId={props.leadId}
                    postModalOpen={handlePostEmailModalOpen}
                    originSalesDocumentType={emailModalType}
                />
            )}
            {/* @ts-expect-error */}
            <div id={NUTSHELL_QUOTE_SCRIPT_CONTAINER_ID} ref={scriptRef} />
        </>
    );
}
